import React, { MutableRefObject, ReactNode, useCallback } from 'react';
import { isMobile } from 'react-device-detect';
import useAsyncEffect from 'use-async-effect';

import { useConference } from '../../../DashboardContent/Dolby';
import { useMytaverseEvent } from '../../../../providers';
import { sendCustomSentryBreadcrumb } from '@helpers/sentry';
import WarningButtonBadge from '../../../WarningButtonBadge';
import ShareWebcamButtonNew from './ShareWebcamButtonNew/ShareWebcamButton';
import { useConferenceState } from '@hooks/conferenceContext';
import { useCommunications } from '@core/communications/providers/CommunicationsProvider';
import { AnalyticsType } from '@interfaces/analytics';

//TODO: clean old components when figma is finalized
const WebcamSharing = () => {
  const { videoStarted } = useConference();

  const { trackEventAnalytics } = useMytaverseEvent();

  const { handleToggleVideoSharing, isCameraOn, cameraList } =
    useCommunications();

  const { activeCameraDeviceId } = useConferenceState();

  const videoRef = React.useRef<HTMLVideoElement | null>(null);

  const cameraDeviceId = cameraList.length ? cameraList[0]?.deviceId : '';

  const currentTracks = React.useRef<MediaStreamTrack[] | null>(null);

  const handleVideoClick = async () => {
    sendCustomSentryBreadcrumb({
      message: `${!videoStarted ? 'start' : 'stop'} video`,
    });

    await handleToggleVideoSharing(
      videoRef as MutableRefObject<HTMLVideoElement>,
    );
  };

  const showWarningButtonBadge = useCallback(
    (icon: ReactNode) => {
      return (
        <WarningButtonBadge isHidden={!!cameraDeviceId}>
          {icon}
        </WarningButtonBadge>
      );
    },
    [cameraDeviceId, isMobile],
  );

  useAsyncEffect(async () => {
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { deviceId: { exact: activeCameraDeviceId } },
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          currentTracks.current = stream.getTracks();

          trackEventAnalytics(AnalyticsType.CameraOn);
        }
      } catch (err) {
        console.error('Error accessing camera:', err);
      }
    };

    const stopCamera = () => {
      if (currentTracks.current) {
        currentTracks.current.forEach((track) => {
          track.stop();
        });
        currentTracks.current = null;

        trackEventAnalytics(AnalyticsType.CameraOff);
      }
    };

    if (isCameraOn) {
      await startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [isCameraOn, videoRef, activeCameraDeviceId, trackEventAnalytics]);

  return (
    <ShareWebcamButtonNew
      cameraDeviceId={cameraDeviceId ? (activeCameraDeviceId as string) : ''}
      onVideoClick={handleVideoClick}
      showWarningBadge={showWarningButtonBadge}
    />
  );
};

export default WebcamSharing;
